import PropTypes from 'prop-types';
import React from 'react';

import { Alert, DecisionStamp } from 'common-src/components/base';

import MocaChoice from './MocaChoice';
import {
  formatFinalCall,
  formatStamp,
  getMocaPromptSource,
  getOutcomeState,
  getProviderLineForTeam,
  getTeamStamp,
  IN_PERSON_LABEL,
  TEAM_BADGE_LABELS,
} from './mocaPrompt';
import styles from './MocaPrompt.module.scss';
import WatchLink from './WatchLink';

const TEAM_OPTIONS = [
  { value: 'plan', label: 'Plan in person' },
  { value: 'skip', label: 'Not needed' },
  { value: 'edit', label: 'Edit' },
];

const TEAM_TONES = { plan: 'success', skip: 'outline', edit: 'neutral' };

const LockedDecision = ({ moca }) => {
  const { team, provider } = moca.state;
  const { tone, label } = getOutcomeState(moca.state);

  return (
    <div className={styles.lockedBlock}>
      <DecisionStamp
        label={label}
        tone={tone}
        withIcon
        stamp={team ? getTeamStamp(team) : undefined}
      />
      <p className={styles.lockedLine}>{getProviderLineForTeam(moca.state)}</p>
      <p className={styles.lockedFinal}>{formatFinalCall(provider)}</p>
    </div>
  );
};

LockedDecision.propTypes = { moca: PropTypes.object.isRequired };

const TeamDecision = ({ moca }) => {
  const { team } = moca.state;

  return (
    <DecisionStamp
      label={TEAM_BADGE_LABELS[team.choice]}
      tone={TEAM_TONES[team.choice]}
      withIcon
      note={team.note}
      stamp={formatStamp(team)}
      disabled={moca.pending}
      onUndo={() => moca.undo('team')}
    />
  );
};

TeamDecision.propTypes = { moca: PropTypes.object.isRequired };

const OpenDecision = ({ latestMoca, moca, reviewUrl }) => (
  <Alert
    variant="warning"
    title={IN_PERSON_LABEL}
    actions={
      <>
        <MocaChoice
          label="Care team decision"
          options={TEAM_OPTIONS}
          placeholder="What should happen instead?"
          disabled={moca.pending}
          onChoose={(choice, note) => moca.decide('team', choice, note)}
        />
        {reviewUrl && <WatchLink reviewUrl={reviewUrl} label="Watch" />}
      </>
    }
  >
    {getMocaPromptSource(latestMoca)}
  </Alert>
);

OpenDecision.propTypes = {
  latestMoca: PropTypes.object,
  moca: PropTypes.object.isRequired,
  reviewUrl: PropTypes.string,
};

const MocaDecision = ({ latestMoca, moca, reviewUrl, hideWhenNotNeeded = false, inset = true }) => {
  const { team, provider, hiddenInPrepare } = moca.state;
  if (hideWhenNotNeeded && hiddenInPrepare) return null;

  let content = <OpenDecision latestMoca={latestMoca} moca={moca} reviewUrl={reviewUrl} />;
  if (provider) content = <LockedDecision moca={moca} />;
  else if (team) content = <TeamDecision moca={moca} />;

  return <div className={inset ? styles.decision : undefined}>{content}</div>;
};

MocaDecision.propTypes = {
  latestMoca: PropTypes.object,
  moca: PropTypes.shape({
    state: PropTypes.object.isRequired,
    pending: PropTypes.bool,
    decide: PropTypes.func.isRequired,
    undo: PropTypes.func.isRequired,
  }).isRequired,
  reviewUrl: PropTypes.string,
  hideWhenNotNeeded: PropTypes.bool,
  inset: PropTypes.bool,
};

export default MocaDecision;
